import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import {
  Package,
  PackageCreate,
  PackageFilter,
  PackageUpdate,
  SurveillanceLog,
  SurveillanceLogCreate,
  SurveillanceLogFilter,
  SurveillanceLogUpdate,
  Visit,
  VisitCreate,
  VisitFilter,
  VisitUpdate,
} from '@core/types/security-module.type';

@Injectable({ providedIn: 'root' })
export class SecurityModuleService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.api;

  // ===== Visits =====
  visitsGrid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Visit>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as VisitFilter;
    if (filter.unitId) params = params.set('filter.unitId', filter.unitId);
    if (filter.status !== undefined) params = params.set('filter.status', String(filter.status));
    if (filter.from) params = params.set('filter.from', filter.from);
    if (filter.to) params = params.set('filter.to', filter.to);

    return this.http.get<ApiResponse<PaginationResult<Visit>>>(`${this.apiUrl}/visits/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading visits')),
    );
  }

  createVisit(data: VisitCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/visits`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating visit')),
    );
  }

  updateVisit(data: VisitUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/visits`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating visit')),
    );
  }

  deleteVisit(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/visits/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting visit')),
    );
  }

  // ===== Packages =====
  packagesGrid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Package>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as PackageFilter;
    if (filter.unitId) params = params.set('filter.unitId', filter.unitId);
    if (filter.status !== undefined) params = params.set('filter.status', String(filter.status));
    if (filter.from) params = params.set('filter.from', filter.from);
    if (filter.to) params = params.set('filter.to', filter.to);

    return this.http.get<ApiResponse<PaginationResult<Package>>>(`${this.apiUrl}/packages/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading packages')),
    );
  }

  createPackage(data: PackageCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/packages`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating package')),
    );
  }

  updatePackage(data: PackageUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/packages`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating package')),
    );
  }

  deletePackage(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/packages/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting package')),
    );
  }

  // ===== Surveillance Logs =====
  surveillanceLogsGrid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<SurveillanceLog>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as SurveillanceLogFilter;
    if (filter.guardId) params = params.set('filter.guardId', filter.guardId);
    if (filter.eventType) params = params.set('filter.eventType', filter.eventType);
    if (filter.from) params = params.set('filter.from', filter.from);
    if (filter.to) params = params.set('filter.to', filter.to);

    return this.http.get<ApiResponse<PaginationResult<SurveillanceLog>>>(`${this.apiUrl}/surveillance-logs/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading surveillance logs')),
    );
  }

  createSurveillanceLog(data: SurveillanceLogCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.apiUrl}/surveillance-logs`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating log')),
    );
  }

  updateSurveillanceLog(data: SurveillanceLogUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(`${this.apiUrl}/surveillance-logs`, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating log')),
    );
  }

  deleteSurveillanceLog(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/surveillance-logs/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting log')),
    );
  }
}

